import React, { useState, useEffect } from 'react';
import { usePlatformAuth } from '@/context/PlatformAuthContext';
import PlatformLayout from '@/components/layout/PlatformLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { 
  Loader2, UserCheck, Plus, Users, TrendingUp, Tag, 
  DollarSign, Target, Mail, Phone
} from 'lucide-react';
import { toast } from 'sonner';

const SalesReps = () => {
  const { fetchWithAuth, isSuperAdmin, isSalesManager, isSalesRep, user } = usePlatformAuth();
  const [reps, setReps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    coupon_code: '',
    discount_percent: 10,
    discount_duration_months: 3,
    monthly_target_customers: 5,
    commission_percent: 15
  });

  useEffect(() => {
    loadSalesReps();
  }, []);

  const loadSalesReps = async () => {
    setLoading(true);
    try {
      const response = await fetchWithAuth('/api/platform/sales-reps');
      if (!response.ok) throw new Error('Failed to load sales reps');
      
      const data = await response.json();
      setReps(data.sales_reps || []);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.name || !formData.email || !formData.coupon_code) {
      toast.error('Please fill in all required fields');
      return;
    }

    setCreating(true);
    try {
      const response = await fetchWithAuth('/api/platform/sales-reps', {
        method: 'POST',
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create sales rep');
      }

      toast.success('Sales rep created successfully');
      setShowCreateDialog(false);
      setFormData({
        name: '',
        email: '',
        phone: '',
        coupon_code: '',
        discount_percent: 10,
        discount_duration_months: 3,
        monthly_target_customers: 5,
        commission_percent: 15
      });
      loadSalesReps();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCreating(false);
    }
  };

  const formatCurrency = (paise) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(paise / 100);
  };

  return (
    <PlatformLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">Sales Representatives</h1>
            <p className="text-slate-400 mt-1">Manage your sales team and track performance</p>
          </div>
          
          {(isSuperAdmin || isSalesManager) && (
            <Button 
              onClick={() => setShowCreateDialog(true)}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="create-sales-rep"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Sales Rep
            </Button>
          )}
        </div>

        {/* Sales Reps Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        ) : reps.length === 0 ? (
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="py-12">
              <div className="text-center">
                <UserCheck className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">No sales representatives yet</p>
                {(isSuperAdmin || isSalesManager) && (
                  <Button 
                    onClick={() => setShowCreateDialog(true)}
                    className="mt-4 bg-blue-600 hover:bg-blue-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Your First Sales Rep
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {reps.map((rep) => (
              <Card key={rep.sales_rep_id} className="bg-slate-800 border-slate-700">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-white font-bold">
                        {rep.name?.charAt(0)?.toUpperCase() || 'S'}
                      </div>
                      <div>
                        <CardTitle className="text-white text-lg">{rep.name}</CardTitle>
                        <CardDescription className="text-slate-400">{rep.email}</CardDescription>
                      </div>
                    </div>
                    <Badge className={rep.is_active ? 'bg-green-500/10 text-green-400' : 'bg-slate-600 text-slate-300'}>
                      {rep.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Coupon Code */}
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-purple-500/10">
                    <Tag className="w-4 h-4 text-purple-400" />
                    <span className="text-purple-300 font-mono font-medium">{rep.coupon_code}</span>
                    <Badge className="ml-auto bg-purple-500/20 text-purple-300">
                      {rep.discount_percent}% off
                    </Badge>
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 rounded-lg bg-slate-700/50">
                      <div className="flex items-center gap-2 text-slate-400 mb-1">
                        <Users className="w-4 h-4" />
                        <span className="text-xs">Customers</span>
                      </div>
                      <p className="text-xl font-bold text-white">{rep.customers_acquired || 0}</p>
                    </div>
                    
                    <div className="p-3 rounded-lg bg-slate-700/50">
                      <div className="flex items-center gap-2 text-slate-400 mb-1">
                        <Target className="w-4 h-4" />
                        <span className="text-xs">Target</span>
                      </div>
                      <p className="text-xl font-bold text-white">{rep.monthly_target_customers}/mo</p>
                    </div>
                    
                    <div className="p-3 rounded-lg bg-slate-700/50">
                      <div className="flex items-center gap-2 text-slate-400 mb-1">
                        <TrendingUp className="w-4 h-4" />
                        <span className="text-xs">Revenue</span>
                      </div>
                      <p className="text-lg font-bold text-white">
                        {formatCurrency(rep.revenue_generated || 0)}
                      </p>
                    </div>
                    
                    <div className="p-3 rounded-lg bg-green-500/10">
                      <div className="flex items-center gap-2 text-green-400 mb-1">
                        <DollarSign className="w-4 h-4" />
                        <span className="text-xs">Commission</span>
                      </div>
                      <p className="text-lg font-bold text-green-400">
                        {formatCurrency(rep.commission_earned || 0)}
                      </p>
                    </div>
                  </div>

                  {/* Commission Rate */}
                  <div className="flex items-center justify-between text-sm text-slate-400 pt-2 border-t border-slate-700">
                    <span>Commission Rate</span>
                    <span className="text-white">{rep.commission_percent}%</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create Sales Rep Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Add Sales Representative</DialogTitle>
            <DialogDescription className="text-slate-400">
              Create a new sales rep with their unique coupon code
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="John Doe"
                className="bg-slate-700/50 border-slate-600 text-white"
                data-testid="sales-rep-name"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="john@example.com"
                className="bg-slate-700/50 border-slate-600 text-white"
                data-testid="sales-rep-email"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Phone</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+91 98765 43210"
                className="bg-slate-700/50 border-slate-600 text-white"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Coupon Code *</Label>
              <Input
                value={formData.coupon_code}
                onChange={(e) => setFormData({ ...formData, coupon_code: e.target.value.toUpperCase() })}
                placeholder="JOHN10"
                className="bg-slate-700/50 border-slate-600 text-white font-mono"
                data-testid="sales-rep-coupon"
              />
              <p className="text-xs text-slate-500">Unique code for customer discounts</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Discount %</Label>
                <Input
                  type="number"
                  value={formData.discount_percent}
                  onChange={(e) => setFormData({ ...formData, discount_percent: parseFloat(e.target.value) })}
                  className="bg-slate-700/50 border-slate-600 text-white"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Duration (months)</Label>
                <Input
                  type="number"
                  value={formData.discount_duration_months}
                  onChange={(e) => setFormData({ ...formData, discount_duration_months: parseInt(e.target.value) })}
                  className="bg-slate-700/50 border-slate-600 text-white"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Monthly Target</Label>
                <Input
                  type="number"
                  value={formData.monthly_target_customers}
                  onChange={(e) => setFormData({ ...formData, monthly_target_customers: parseInt(e.target.value) })}
                  className="bg-slate-700/50 border-slate-600 text-white"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Commission %</Label>
                <Input
                  type="number"
                  value={formData.commission_percent}
                  onChange={(e) => setFormData({ ...formData, commission_percent: parseFloat(e.target.value) })}
                  className="bg-slate-700/50 border-slate-600 text-white"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCreateDialog(false)}
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={creating}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="submit-sales-rep"
            >
              {creating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Sales Rep'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PlatformLayout>
  );
};

export default SalesReps;
